import {
    Autocomplete,
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    TextField,
    Typography
} from "@mui/material";
import React, {useEffect, useRef, useState} from "react";
import {Clear} from "@mui/icons-material";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import {DatePicker} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import Chip from "@mui/material/Chip";
import {useGetEmissionsFiltersMetaQuery} from "../../emissions/api/emissionsApiSlice";
import {useGetMediaTreeQuery} from "../../drafts/api/draftsApiSlice";
import {FilterItemChip, MediaNode} from "../../drafts/types";
import {useGetCmEnumsQuery} from "../../../app/appApiSlice";
import {useDispatch, useSelector} from "react-redux";
import {initialImpressionsState, selectCurrentImpressionsFiltersState, setFilters} from "../store/impressionsSlice";
import {useTranslation} from "react-i18next";
import {ImpressionsFilters} from "../types";
import {Bar, BarChart, CartesianGrid, Label, Legend, Tooltip, XAxis, YAxis} from "recharts";
import {useGetImpressionsQuery, useLazyGetImpressionsXlsxQuery} from "../api/impressionsApiSlice";
import "react-checkbox-tree/lib/react-checkbox-tree.css";
import CheckboxTree from "react-checkbox-tree";
import useDebounce from "../../../hooks/useDebounce";
import {getUuid} from "../../../config/general";

const Impressions: React.FC = () => {

    const [searchByName, setSearchByName] = React.useState<string>('');

    const [getXlsxImpressions, {data: filesMetaData}] = useLazyGetImpressionsXlsxQuery();

    const [data, setData] = React.useState<any[]>([]);

    const [openMediaTreeModal, setOpenMediaTreeModal] = useState(false);
    const [checkedNodes, setCheckedNodes] = useState<string[]>([]);
    const [expanded, setExpanded] = useState<string[]>([]);
    const [nodes, setNodes] = useState<any[]>([]);
    const [filteredNodes, setFilteredNodes] = useState<any[]>([]);
    const [filterNodesText, setFilterNodesText] = useState('');
    const [mediaLength, setMediaLength] = useState(0);
    const checkboxTreeRef = useRef<any>();


    const currentFiltersState = useSelector(selectCurrentImpressionsFiltersState);

    const {isLoading: loadingCmEnums, data: cmEnums} =
        useGetCmEnumsQuery();

    const {
        isLoading: loadingFiltersMeta,
        isError: isFiltersMetaError,
        isSuccess: isSuccessFiltersMeta,
        data: filtersMetaData
    } =
        useGetEmissionsFiltersMetaQuery();

    const {t} = useTranslation();

    let params: string[] = [];
    let filterItemChips: FilterItemChip[] = []
    for (const queryKey in currentFiltersState) {
        const value = currentFiltersState[queryKey as keyof typeof currentFiltersState]
        if (Array.isArray(value)) {
            value.forEach(item => {
                params.push(`${queryKey}[]=${item.toString()}`)
                filterItemChips.push({name: queryKey, value: item, isArray: true})
            })
        } else if (queryKey === 'page') {
            params.push(`page=${(value || 0).toString()}`)
        } else if (queryKey === 'format' && value) {
            params.push(`format=${value.toString()}`)
        } else if (value) {
            params.push(`${queryKey}=${value.toString()}`)
            let val = queryKey === 'granularity' ? t('filters.values.' + value) : value.toString()

            filterItemChips.push({name: queryKey, value: val, isArray: false})
        }
    }

    const mediaFilterItemChips: FilterItemChip[] = filterItemChips.filter(el => el.name === 'mediumIds')

    const {
        isLoading: loadingImpressions,
        data: impressionsResponseData
    } = useGetImpressionsQuery(params.join('&').toString());

    const mediaIdsFromFilters = impressionsResponseData?.filters?.filtering?.fields?.mediumIds?.allowedValues?.map(
        ([, id]) => id
    );

    const {data: mediaTree} = useGetMediaTreeQuery(
        mediaIdsFromFilters?.length ? {mediaIds: mediaIdsFromFilters} : undefined,
        {skip: !mediaIdsFromFilters?.length}
    );
    useEffect(() => {
        if (impressionsResponseData?.data) {
            let _data = [];
            for (const property in impressionsResponseData.data) {
                if (impressionsResponseData.data[property]) {
                    _data.push({
                        name: property.replace(/(\d{4})(\d{2})(\d{2})/, "$1-$2-$3"),
                        ['wyswietlenia']: impressionsResponseData.data[property],
                    });
                }
            }
            setData(_data);
        }

    }, [impressionsResponseData]);

    function removeFilter(filterElement: FilterItemChip) {
        if (filterElement.isArray) {
            if (filterElement.name === 'creationIds') {
                setCheckedNodes([]);
                dispatch(setFilters({
                    ...currentFiltersState,
                    format: 'json',
                    mediumIds: [],
                    [filterElement.name as string]: (currentFiltersState[filterElement.name as keyof ImpressionsFilters] as string[]).filter(el => el !== filterElement.value)
                }))
            } else if (filterElement.name === 'mediumIds') {
                setCheckedNodes([]);
                dispatch(setFilters({
                    ...currentFiltersState,
                    format: 'json',
                    [filterElement.name]: []
                }))
            } else {
                dispatch(setFilters({
                    ...currentFiltersState,
                    format: 'json',
                    [filterElement.name]: (currentFiltersState[filterElement.name as keyof ImpressionsFilters] as string[]).filter(el => el !== filterElement.value)
                }))
            }
        } else {
            if (filterElement.name === 'nameCont') {
                setSearchByName('');
            }
            if (filterElement.name === 'draftId') {
                setCheckedNodes([]);
                dispatch(setFilters({
                    ...currentFiltersState,
                    format: 'json',
                    creationIds: [],
                    mediumIds: [],
                    [filterElement.name as string]: ''
                }))
            } else {
                dispatch(setFilters({
                    ...currentFiltersState,
                    format: 'json',
                    [filterElement.name]: ''
                }))
            }
        }
    }

    const dispatch = useDispatch();


    let mediaSize = 0;
    let flattenNodeValues: string[] = [];

    const buildMediaTreeCheckboxNodes = (tree: MediaNode[]): any[] => {
        return tree.map((element: MediaNode, index) => {
            if (element.children && element.children.length > 0) {
                const val = element.id + '_' + element.type + getUuid(index);
                let place = '';
                if (element.type === 'Media::Agglomeration') {
                    place = '(Aglomeracja)';
                } else if (element.type === 'Media::City') {
                    place = '(Miasto)';
                }
                return {
                    value: val,
                    label: element.name + ' ' + place,
                    children: buildMediaTreeCheckboxNodes(element.children),
                };
            } else {
                const val = element.id + '_' + element.type + getUuid(index);
                mediaSize += 1;
                flattenNodeValues.push(val);
                return {
                    value: val,
                    label: element.asId ? element.asId + ' - ' + element.name : element.name,
                };
            }
        });
    };

    useEffect(() => {
        if (mediaTree && mediaTree.length > 0) {
            mediaSize = 0;
            flattenNodeValues = [];
            const mediaTreeCheckboxNodes = buildMediaTreeCheckboxNodes(mediaTree);
            setNodes(mediaTreeCheckboxNodes);
            setFilteredNodes([{
                label: 'Wszystkie nośniki',
                value: 'all',
                children: mediaTreeCheckboxNodes
            }]);
            setMediaLength(mediaSize);

            const currentIds = new Set(currentFiltersState.mediumIds);
            setCheckedNodes(flattenNodeValues.filter(val => {
                const id = parseInt(val.split('_')[0]);
                return currentIds.has(id);
            }));
        } else {
            setNodes([]);
            setFilteredNodes([]);
            setMediaLength(0);
        }
    }, [mediaTree]);

    useEffect(() => {
        const checkedMedia = checkedNodes
            .filter(el => el !== 'all')
            .map((node: string) => parseInt(node.split('_')[0]))
            .filter(id => !isNaN(id));

        const currentIds = [...currentFiltersState.mediumIds].sort();
        const newIds = [...checkedMedia].sort();
        if (JSON.stringify(currentIds) !== JSON.stringify(newIds)) {
            dispatch(setFilters({
                ...currentFiltersState,
                format: 'json',
                mediumIds: checkedMedia
            }));
        }
    }, [checkedNodes]);

    useEffect(() => {
        return () => {
            dispatch(setFilters(initialImpressionsState.filters))
        }
    }, []);

    const handleOpenMediaModal = () => setOpenMediaTreeModal(true);
    const handleCloseMediaModal = () => setOpenMediaTreeModal(false);

    const onFilterNodeChange = (event: any) => {
        setFilterNodesText(event.target.value)
    }

    const filterNodes = (filtered: any, node: any) => {
        if (node.label.toLocaleLowerCase().match(filterNodesText.toString().toLocaleLowerCase())) {
            filtered.push(node)
            return filtered;
        }
        if (node.children && node.children.length > 0) {
            const children = (node.children || []).reduce(filterNodes, []);
            if (children.length > 0) {
                filtered.push({...node, children});
            }
        }
        return filtered;
    }

    useDebounce(() => {
        if (!filterNodesText) {
            setFilteredNodes([{
                label: 'Wszystkie nośniki',
                value: 'all',
                children: nodes
            }]);
            return;
        }
        setFilteredNodes([{
            label: 'Wszystkie nośniki',
            value: 'all',
            children: nodes.reduce(filterNodes, [])
        }]);
        checkboxTreeRef.current?.onExpandAll()
    }, [filterNodesText], 100);

    return (
        <Box className={'Impressions'}>
            <Box className={'page-header'}>
                <h1>Liczba wyświetleń</h1>
                <p>Wybierz nośniki</p>
            </Box>

            {(filtersMetaData) && (<>
                <Grid container spacing={2}>
                    <Grid item xs={4}>
                        <DatePicker
                            maxDate={dayjs(new Date(currentFiltersState.toDate))}
                            onChange={(event) => {
                                dispatch(setFilters({
                                    ...currentFiltersState,
                                    format: 'json',
                                    fromDate: dayjs(event).date() ? dayjs(event).format('YYYY-MM-DD') : ''
                                }))
                            }}
                            value={currentFiltersState.fromDate ? dayjs(currentFiltersState.fromDate) : null}
                            sx={{marginTop: '14px', width: '100%'}} slotProps={{textField: {size: 'small'}}}
                            label="Od"/>
                    </Grid>
                    <Grid item xs={4}>
                        <DatePicker
                            minDate={currentFiltersState.fromDate ? dayjs(currentFiltersState.fromDate) : undefined}
                            onChange={(event) => {
                                dispatch(setFilters({
                                    ...currentFiltersState,
                                    format: 'json',
                                    toDate: dayjs(event).date() ? dayjs(event).format('YYYY-MM-DD') : ''
                                }))
                            }}
                            sx={{marginTop: '14px', width: '100%'}}
                            value={currentFiltersState.toDate ? dayjs(currentFiltersState.toDate) : null}
                            slotProps={{textField: {size: 'small'}}} label="Do"/>
                    </Grid>
                    <Grid item xs={2} sx={{marginTop: '14px'}}>
                        <FormControl fullWidth>
                            <InputLabel shrink id="demo-simple-select-label">Gradacja</InputLabel>
                            <Select
                                size="small"
                                variant={"outlined"}
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                label="Format nośnika"
                                value={currentFiltersState.granularity || ''}
                                notched
                                onChange={(event) => {
                                    dispatch(setFilters({
                                        ...currentFiltersState,
                                        format: 'json',
                                        granularity: event.target.value
                                    }))
                                }}
                            >
                                <MenuItem
                                    key={'day'}
                                    value={'daily'}>Dzień</MenuItem>
                                <MenuItem
                                    key={'week'}
                                    value={'weekly'}>Tydzień</MenuItem>
                                <MenuItem
                                    key={'month'}
                                    value={'monthly'}>Miesiąc</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {impressionsResponseData && <>
                        <Grid item xs={4}>
                            <Box sx={{marginBottom: '15px'}}><Autocomplete
                                fullWidth={true}
                                disablePortal
                                onChange={(_, value) => {
                                    setCheckedNodes([]);
                                    dispatch(setFilters({
                                        ...currentFiltersState,
                                        format: 'json',
                                        creationIds: [],
                                        mediumIds: [],
                                        draftId: value ? value[1] : null
                                    }))
                                }}
                                value={currentFiltersState.draftId ? [impressionsResponseData.filters.filtering.fields.draftId.allowedValues.filter(el => el[1] === currentFiltersState.draftId)[0][0], currentFiltersState.draftId] as [string, number] : null}
                                size="small"
                                options={impressionsResponseData.filters.filtering.fields.draftId.allowedValues}
                                getOptionLabel={(draftsAllowedValues) => {
                                    return `${draftsAllowedValues[0]}`;
                                }}
                                renderOption={(props, option, state, ownerState) => {
                                    const {key, id, ...optionProps} = props;

                                    return (<Box
                                        key={id}
                                        component="li"
                                        {...optionProps}
                                    >
                                        {option[0]}
                                    </Box>)
                                }}
                                renderInput={(params) => <TextField {...params} label="Schemat"/>}
                            /></Box>
                        </Grid>
                        <Grid item xs={2}>
                            <Box sx={{marginBottom: '15px'}}>
                                <Autocomplete
                                    multiple
                                    disabled={!currentFiltersState.draftId}
                                    size={'small'}
                                    fullWidth={true}
                                    options={impressionsResponseData.filters.filtering.fields.creationIds.allowedValues.map((el) => el[1].toString())}
                                    value={currentFiltersState.creationIds.map(el => el.toString())}
                                    onChange={(_, value) => {
                                        setCheckedNodes([]);
                                        dispatch(setFilters({
                                            ...currentFiltersState,
                                            format: 'json',
                                            mediumIds: [],
                                            creationIds: value.map(el => parseInt(el))
                                        }))
                                    }}
                                    filterOptions={(options, {inputValue}) => {
                                        return impressionsResponseData.filters.filtering.fields.creationIds.allowedValues
                                            .filter(item => item[0].toLowerCase().includes(inputValue.toLowerCase())).map(el => el[1].toString());
                                    }}
                                    renderOption={(props, option, state, ownerState) => {
                                        const {key, id, ...optionProps} = props;
                                        const emissionName = impressionsResponseData.filters.filtering.fields.creationIds.allowedValues.filter(el => el[1] === parseInt(option))[0][0]

                                        return (<Box
                                            key={id}
                                            component="li"
                                            {...optionProps}
                                        >
                                            {emissionName}
                                        </Box>)
                                    }}
                                    renderTags={(value: readonly string[]) =>
                                        value.map((option: string, index: number) => {
                                            const emissionName = impressionsResponseData.filters.filtering.fields.creationIds.allowedValues.filter(el => el[1] === parseInt(option))[0][0]
                                            if (index === 0) {
                                                return (
                                                    <Typography sx={{marginLeft: '10px'}}>
                                                        {emissionName}
                                                    </Typography>
                                                )
                                            }

                                            return (
                                                <Typography>
                                                    {`, ${emissionName}`}
                                                </Typography>
                                            );
                                        })
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            label="Emisje"
                                        />
                                    )}
                                /></Box>
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                size="small"
                                fullWidth
                                label={currentFiltersState.mediumIds.length > 0 ? 'Media' : ''}
                                value={currentFiltersState.mediumIds.length > 0 ? `${currentFiltersState.mediumIds.length}/${mediaLength}` : 'Media'}
                                onClick={() => {
                                    if (currentFiltersState.draftId && currentFiltersState.creationIds.length) {
                                        handleOpenMediaModal()
                                    }
                                }}
                                disabled={!currentFiltersState.draftId || !currentFiltersState.creationIds.length || !currentFiltersState.mediumIds.length}
                                variant="outlined"
                                InputProps={{readOnly: true}}
                                InputLabelProps={{shrink: currentFiltersState.mediumIds.length > 0}}
                                sx={{cursor: 'pointer', '& input': {cursor: 'pointer'}}}
                            />
                            <Dialog
                                open={openMediaTreeModal}
                                onClose={handleCloseMediaModal}
                                fullWidth={true}
                                maxWidth="md"
                            >
                                <DialogTitle>Wybierz nośniki</DialogTitle>
                                <DialogContent>
                                    <Box sx={{minHeight: '300px'}}>
                                        {filteredNodes.length > 0 && (
                                            <>
                                                <TextField
                                                    value={filterNodesText}
                                                    label="Wyszukaj..."
                                                    onInput={onFilterNodeChange}
                                                    variant="outlined"
                                                    sx={{mt: 1, mb: 3}}
                                                    fullWidth
                                                />
                                                <CheckboxTree
                                                    ref={checkboxTreeRef}
                                                    showNodeTitles={true}
                                                    nodes={filteredNodes}
                                                    checked={checkedNodes}
                                                    expanded={expanded}
                                                    showExpandAll={true}
                                                    onCheck={(checked: any) => {
                                                        setCheckedNodes(checked)
                                                    }}
                                                    onExpand={(expanded: any) => {
                                                        setExpanded(expanded)
                                                    }}
                                                />
                                            </>
                                        )}
                                    </Box>
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={handleCloseMediaModal}>Zamknij</Button>
                                </DialogActions>
                            </Dialog>
                        </Grid>
                    </>}


                </Grid>

                <Grid container spacing={2} pb={2}>
                    {filterItemChips.map((filter, index) => {

                        let chipValue: string | number | undefined = filter.value

                        if (filter.name === 'emissionStatusEq') {
                            chipValue = t(`emissions.status.${filter.value}`)
                        } else if (filter.name === 'draftIdIn') {
                            chipValue = filtersMetaData.data.filtering.fields.draftIdIn.allowedValues.filter(el => el[1] === parseInt(filter.value as string))[0][0]
                        } else if ((filter.name === 'creationIds' || filter.name === 'draftId') && impressionsResponseData) {
                            const element = impressionsResponseData.filters.filtering.fields[filter.name].allowedValues.find(el => el[1] === parseInt(filter.value as string))
                            chipValue = element ? element[0] : ''
                        } else if (filter.name === 'mediumIds' && impressionsResponseData) {
                            return
                        }


                        return (
                            <Grid item key={filter.value}>
                                <Chip
                                    color="info"
                                    label={(
                                        <React.Fragment>{t(`filters.keys.${filter.name}`) + ': '}<strong>{chipValue}</strong></React.Fragment>)}
                                    onClick={() => {
                                    }}
                                    onDelete={() => removeFilter(filter)}
                                />
                            </Grid>)
                    })}
                    {
                        mediaFilterItemChips.length === 1 && impressionsResponseData && (
                            <Grid item key={mediaFilterItemChips[0].value}>
                                <Chip
                                    color="info"
                                    label={(
                                        <React.Fragment>{t(`filters.keys.${mediaFilterItemChips[0].name}`) + ': '}<strong>{impressionsResponseData.filters.filtering.fields['mediumIds'].allowedValues.find(el => el[1] === parseInt(mediaFilterItemChips[0].value as string))![0]}</strong></React.Fragment>)}
                                    onClick={() => {
                                    }}
                                    onDelete={() => removeFilter(mediaFilterItemChips[0])}
                                />
                            </Grid>)
                    }

                    {
                        mediaFilterItemChips.length > 1 && impressionsResponseData && (
                            <Grid item key={mediaFilterItemChips[0].value}>
                                <Chip
                                    color="info"
                                    label={(
                                        <React.Fragment>{t(`filters.keys.${mediaFilterItemChips[0].name}`) + ': '}<strong>{impressionsResponseData.filters.filtering.fields['mediumIds'].allowedValues.find(el => el[1] === parseInt(mediaFilterItemChips[0].value as string))![0]} | &nbsp;
                                            {impressionsResponseData.filters.filtering.fields['mediumIds'].allowedValues.find(el => el[1] === parseInt(mediaFilterItemChips[1].value as string))![0]}...</strong></React.Fragment>)}
                                    onClick={() => {
                                    }}
                                    onDelete={() => removeFilter(mediaFilterItemChips[0])}
                                />
                            </Grid>)
                    }

                </Grid>
                <Backdrop
                    sx={(theme) => ({color: '#fff', zIndex: theme.zIndex.drawer + 1})}
                    open={false}
                >
                    <CircularProgress color="inherit"/>
                </Backdrop>


            </>)}
            <Grid spacing={1} container xs={10} justifyContent="flex-end">
                <Grid item xs={2} sx={{display: 'flex', justifyContent: 'flex-end'}}>
                    <Button
                        variant="text"
                        onClick={async () => {
                            getXlsxImpressions(params.join('&').toString()).unwrap().then((data) => {
                            })
                            // setMockAlerts([])
                        }}
                    >
                        <span>Pobierz XLSX</span>
                    </Button></Grid>
            </Grid>

            {(<Box sx={{display: 'flex', justifyContent: 'center', paddingTop: '160px'}} className={'chart-container'}>
                <BarChart
                    width={1200}
                    height={600}
                    data={data}
                    margin={{
                        top: 20,
                        right: 30,
                        left: 20,
                        bottom: 5,
                    }}
                >
                    <CartesianGrid strokeDasharray="3 3"/>
                    <XAxis dataKey="name"/>
                    <YAxis>
                        <Label value={'ilość'} angle={270} position="insideLeft" offset={-5}/>
                    </YAxis>
                    {data.length > 0 && (<Tooltip/>)}
                    <Legend/>
                    <Bar dataKey="wyswietlenia" stackId="a" fill="#82ca9d"/>
                </BarChart>
            </Box>)}

        </Box>
    );
};

export default Impressions;
